import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, MessageSquare, ThumbsUp, ThumbsDown } from "lucide-react";
import axios from "../../utility/customAxios";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { cn } from "../ui/lib/utils";
import { useToast } from "../ui/use-toast";

interface InvestigationFeedbackProps {
    investigationUuid: string;
    isAIGenerated: boolean;
}

interface FeedbackData {
    uuid: string;
    organizationUuid: string;
    investigationUuid: string;
    userUuid: string;
    correct: string;
    comment: string;
    createdAt: string;
    updatedAt: string;
}

export const InvestigationFeedback: React.FC<InvestigationFeedbackProps> = ({
    investigationUuid,
    isAIGenerated
}) => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [correct, setCorrect] = useState<string | null>(null);
    const [comment, setComment] = useState("");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Fetch existing feedback
    const { data: existingFeedback, isLoading } = useQuery<FeedbackData | null>({
        queryKey: ["investigation-feedback", investigationUuid],
        queryFn: async () => {
            try {
                const response = await axios.get(`/api/v1/investigation/feedback?uuid=${investigationUuid}`);
                return response.data;
            } catch (error: any) {
                if (error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: isAIGenerated && !!investigationUuid
    });

    // Set initial values when feedback is loaded
    useEffect(() => {
        if (existingFeedback) {
            setCorrect(existingFeedback.correct);
            setComment(existingFeedback.comment);
        }
    }, [existingFeedback]);

    // Create feedback mutation
    const createMutation = useMutation({
        mutationFn: async (data: { correct: string; comment: string }) => {
            return axios.post(`/api/v1/investigation/feedback?uuid=${investigationUuid}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["investigation-feedback", investigationUuid] });
            toast({
                title: "Feedback submitted",
                description: "Thank you for your feedback!",
            });
            setIsEditing(false);
            setHasUnsavedChanges(false);
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to submit feedback",
                variant: "destructive"
            });
        }
    });

    // Update feedback mutation
    const updateMutation = useMutation({
        mutationFn: async (data: { correct?: string; comment?: string }) => {
            if (!existingFeedback) return;
            return axios.put(`/api/v1/investigation/feedback?uuid=${existingFeedback.uuid}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["investigation-feedback", investigationUuid] });
            toast({
                title: "Feedback updated",
                description: "Your feedback has been updated",
            });
            setIsEditing(false);
            setHasUnsavedChanges(false);
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to update feedback",
                variant: "destructive"
            });
        }
    });

    // Delete feedback mutation
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!existingFeedback) return;
            return axios.delete(`/api/v1/investigation/feedback?uuid=${existingFeedback.uuid}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["investigation-feedback", investigationUuid] });
            toast({
                title: "Feedback deleted",
                description: "Your feedback has been removed",
            });
            setCorrect(null);
            setComment("");
            setIsEditing(false);
            setHasUnsavedChanges(false);
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to delete feedback",
                variant: "destructive"
            });
        }
    });

    const handleSave = () => {
        if (correct === null) {
            toast({
                title: "Please select a rating",
                description: "Was this investigation helpful?",
                variant: "destructive"
            });
            return;
        }

        const data = {
            correct,
            comment: comment.trim()
        };

        if (existingFeedback) {
            updateMutation.mutate(data);
        } else {
            createMutation.mutate(data);
        }
    };

    const handleCancel = () => {
        if (existingFeedback) {
            setCorrect(existingFeedback.correct);
            setComment(existingFeedback.comment);
        } else {
            setCorrect(null);
            setComment("");
        }
        setIsEditing(false);
        setHasUnsavedChanges(false);
    };

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete your feedback?")) {
            deleteMutation.mutate();
        }
    };

    if (!isAIGenerated) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="border-b border-border pb-4">
                <h3 className="text-sm font-medium text-textlight mb-2">AI Feedback</h3>
                <p className="text-xs text-textdark">Loading...</p>
            </div>
        );
    }

    const isSubmitting = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    return (
        <div className="border-b border-border pb-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-textlight">AI Feedback</h3>
                {existingFeedback && !isEditing && (
                    <div className="flex gap-1">
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() => setIsEditing(true)}
                        >
                            Edit
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-red-400 hover:text-red-300"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </div>
                )}
            </div>

            {isEditing || !existingFeedback ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-textmedium">Was this helpful?</span>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant={correct === "true" ? "default" : "outline"}
                                className={cn(
                                    "h-8 px-3",
                                    correct === "true" ? "bg-green-600 hover:bg-green-700" : ""
                                )}
                                onClick={() => {
                                    setCorrect("true");
                                    setHasUnsavedChanges(true);
                                }}
                                disabled={isSubmitting}
                            >
                                <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                                Yes
                            </Button>
                            <Button
                                size="sm"
                                variant={correct === "false" ? "default" : "outline"}
                                className={cn(
                                    "h-8 px-3",
                                    correct === "false" ? "bg-red-600 hover:bg-red-700" : ""
                                )}
                                onClick={() => {
                                    setCorrect("false");
                                    setHasUnsavedChanges(true);
                                }}
                                disabled={isSubmitting}
                            >
                                <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                                No
                            </Button>
                        </div>
                    </div>

                    <Textarea
                        placeholder="Additional comments (optional)"
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            setHasUnsavedChanges(true);
                        }}
                        className="min-h-[80px] text-sm bg-backgrounddark border-border"
                        disabled={isSubmitting}
                    />

                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={isSubmitting || correct === null}
                            className="bg-primary hover:bg-primary/90"
                        >
                            {isSubmitting ? "Saving..." : "Save"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        {existingFeedback.correct === "true" ? (
                            <div className="flex items-center gap-1 text-green-500">
                                <ThumbsUp className="h-4 w-4" />
                                <span className="text-sm">Helpful</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 text-red-500">
                                <ThumbsDown className="h-4 w-4" />
                                <span className="text-sm">Not helpful</span>
                            </div>
                        )}
                    </div>
                    {existingFeedback.comment && (
                        <div className="bg-backgrounddark rounded p-2">
                            <p className="text-sm text-textmedium whitespace-pre-wrap">
                                {existingFeedback.comment}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};